import {
	forwardRef,
	type ReactNode,
	type KeyboardEvent,
} from "react";
import { createContext, useContext, useState } from "react";
import { cn } from "@/lib/utils";

// =====================
// Types
// =====================

export interface TabsContextValue {
	activeTab: string;
	setActiveTab: (value: string) => void;
}

interface TabsProps {
	defaultValue?: string;
	value?: string;
	onValueChange?: (value: string) => void;
	children: ReactNode;
	className?: string;
}

interface TabsListProps {
	children: ReactNode;
	className?: string;
	variant?: "default" | "pills" | "underline";
	fullWidth?: boolean;
}

export interface TabsTriggerProps {
	value: string;
	children: ReactNode;
	disabled?: boolean;
	icon?: ReactNode;
	className?: string;
}

export interface TabsContentProps {
	value: string;
	children: ReactNode;
	className?: string;
}

export type { TabsProps, TabsListProps };

// =====================
// Context
// =====================

const TabsContext = createContext<{ activeTab: string; setActiveTab: (v: string) => void } | null>(null);

// =====================
// Tabs Root
// =====================

export function Tabs({
	defaultValue = "",
	value: controlledValue,
	onValueChange,
	children,
	className,
}: TabsProps) {
	const [internalValue, setInternalValue] = useState(defaultValue);
	const activeTab = controlledValue ?? internalValue;

	const setActiveTab = (newValue: string) => {
		if (controlledValue === undefined) {
			setInternalValue(newValue);
		}
		onValueChange?.(newValue);
	};

	return (
		<TabsContext.Provider value={{ activeTab, setActiveTab }}>
			<div className={cn("w-full", className)}>{children}</div>
		</TabsContext.Provider>
	);
}

// =====================
// Tabs List
// =====================

export function TabsList({
	children,
	className,
	variant = "default",
	fullWidth = false,
}: TabsListProps) {
	return (
		<div
			role="tablist"
			className={cn(
				"flex gap-1",
				variant === "default" && "p-1 bg-gray-100 dark:bg-gray-800 rounded-lg",
				variant === "underline" && "border-b border-gray-200 dark:border-gray-700 gap-0",
				fullWidth && "w-full",
				className
			)}
		>
			{children}
		</div>
	);
}

// =====================
// Tabs Trigger
// =====================

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
	({ value, children, disabled, icon, className, ...props }, ref) => {
		const context = useContext(TabsContext);
		if (!context) throw new Error("TabsTrigger must be used within Tabs");

		const { activeTab, setActiveTab } = context;
		const isActive = activeTab === value;

		const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
			if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
				e.preventDefault();
				const triggers = Array.from(document.querySelectorAll('[role="tab"]:not([disabled])'));
				const currentIndex = triggers.findIndex((t) => t.getAttribute("data-tab-value") === value);
				const nextIndex = e.key === "ArrowRight"
					? (currentIndex + 1) % triggers.length
					: (currentIndex - 1 + triggers.length) % triggers.length;
				const nextTrigger = triggers[nextIndex] as HTMLButtonElement;
				nextTrigger?.focus();
				nextTrigger?.click();
			}
		};

		return (
			<button
				ref={ref}
				role="tab"
				data-tab-value={value}
				data-state={isActive ? "active" : "inactive"}
				type="button"
				disabled={disabled}
				aria-selected={isActive}
				tabIndex={isActive ? 0 : -1}
				onClick={() => setActiveTab(value)}
				onKeyDown={handleKeyDown}
				className={cn(
					"inline-flex items-center justify-center gap-2 font-medium transition-all relative",
					"px-4 py-2.5 text-sm",
					"data-[state=active]:text-brand-700 dark:data-[state=active]:text-brand-300",
					"data-[state=inactive]:text-gray-500 dark:data-[state=inactive]:text-gray-400",
					"data-[state=inactive]:hover:text-gray-700 dark:data-[state=inactive]:hover:text-gray-300",
					"data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-2 data-[state=active]:after:right-2 data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-full data-[state=active]:after:bg-brand-500",
					"disabled:pointer-events-none disabled:opacity-50",
					"focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
					className
				)}
				{...props}
			>
				{icon}
				{children}
			</button>
		);
	}
);

TabsTrigger.displayName = "TabsTrigger";

// =====================
// Tabs Content
// =====================

export function TabsContent({ value, children, className }: TabsContentProps) {
	const context = useContext(TabsContext);
	if (!context) throw new Error("TabsContent must be used within Tabs");

	const { activeTab } = context;
	const isActive = activeTab === value;

	if (!isActive) return null;

	return (
		<div
			role="tabpanel"
			data-state={isActive ? "active" : "inactive"}
			tabIndex={0}
			className={cn("mt-2 focus:outline-none animate-fade-in", className)}
		>
			{children}
		</div>
	);
}